import { AlertTriangle } from "lucide-react";
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Slider } from "../ui/slider";
import { inr, inrCompact } from "../../lib/format";

export function NumField({ label, value, onChange, min, max, step = 1, prefix = "", suffix = "", id }) {
  const clamp = (v) => Math.min(Math.max(Number.isFinite(v) ? v : min, min), max);
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4">
        <label htmlFor={id} className="text-sm font-medium text-slate-600">{label}</label>
        <div className="flex items-center rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 focus-within:border-gold">
          {prefix && <span className="mr-1 text-sm text-slate-400">{prefix}</span>}
          <input id={id} type="number" min={min} max={max} step={step} value={value} data-testid={`${id}-input`}
            onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
            onBlur={() => onChange(clamp(value))}
            className="font-num w-28 bg-transparent text-right text-sm font-semibold text-navy outline-none" />
          {suffix && <span className="ml-1 text-sm text-slate-400">{suffix}</span>}
        </div>
      </div>
      <Slider min={min} max={max} step={step} value={[clamp(value)]} onValueChange={([v]) => onChange(v)} data-testid={`${id}-slider`} />
    </div>
  );
}

export function Stat({ label, value, accent, testid, big }) {
  return (
    <div className={`rounded-2xl p-5 ${accent ? "bg-navy text-white" : "bg-slate-50"}`}>
      <p className={`text-xs uppercase tracking-wider ${accent ? "text-slate-400" : "text-slate-500"}`}>{label}</p>
      <p data-testid={testid} className={`font-num mt-2 font-semibold ${big ? "text-2xl sm:text-3xl" : "text-xl"} ${accent ? "text-mint" : "text-navy"}`}>{value}</p>
    </div>
  );
}

export function GrowthChart({ data, aLabel = "Invested", bLabel = "Projected value" }) {
  return (
    <div className="h-64 w-full" data-testid="calc-growth-chart">
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ left: 0, right: 8, top: 10 }}>
          <defs>
            <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#00C987" stopOpacity={0.45} /><stop offset="100%" stopColor="#00C987" stopOpacity={0} /></linearGradient>
            <linearGradient id="gi" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#D6B36A" stopOpacity={0.4} /><stop offset="100%" stopColor="#D6B36A" stopOpacity={0} /></linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
          <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
          <YAxis tickFormatter={inrCompact} tick={{ fontSize: 11, fill: "#64748b" }} tickLine={false} axisLine={false} width={70} />
          <Tooltip formatter={(v) => inr(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Area type="monotone" dataKey="invested" name={aLabel} stroke="#D6B36A" fill="url(#gi)" strokeWidth={2} />
          <Area type="monotone" dataKey="value" name={bLabel} stroke="#00C987" fill="url(#gv)" strokeWidth={2.5} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function Disclaimer({ children }) {
  return (
    <div data-testid="calc-disclaimer" className="flex gap-3 rounded-2xl border border-gold/40 bg-gold-50 p-4 text-xs leading-relaxed text-slate-700">
      <AlertTriangle size={16} className="mt-0.5 shrink-0 text-gold-600" />
      <p>{children || "Illustrative projection only, based on the assumptions you entered. Returns are not guaranteed and actual results will vary. Mutual fund investments are subject to market risks, read all scheme related documents carefully. This is not investment advice."}</p>
    </div>
  );
}

export function CalcLayout({ title, description, inputs, results, chart, note }) {
  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_1.15fr]">
      <div className="rounded-3xl border border-slate-200 bg-white p-6 sm:p-8">
        <h3 className="text-xl font-bold text-navy">{title}</h3>
        <p className="mt-1 text-sm text-slate-500">{description}</p>
        <div className="mt-8 space-y-7">{inputs}</div>
      </div>
      <div className="space-y-5 rounded-3xl border border-slate-200 bg-white p-6 sm:p-8">
        <div className="grid gap-3 sm:grid-cols-3">{results}</div>
        {chart}
        <Disclaimer>{note}</Disclaimer>
      </div>
    </div>
  );
}
